import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Activity,
  Footprints,
  Flame,
  Timer,
  RefreshCw,
  Link2,
  Unlink,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Calendar,
  Loader2
} from 'lucide-react';

export default function GoogleFitPage() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [todayStats, setTodayStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [selectedDays, setSelectedDays] = useState('7');

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/integrations/google-fit/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      
      if (response.data.connected) {
        await fetchData();
        await fetchTodayStats();
      }
    } catch (error) {
      console.error('Error fetching Google Fit status:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/integrations/google-fit/data?days=${selectedDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching Google Fit data:', error);
    }
  };

  const fetchTodayStats = async () => {
    try {
      const response = await axios.get(`${API}/integrations/google-fit/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayStats(response.data);
    } catch (error) {
      console.error('Error fetching today stats:', error);
    }
  };

  useEffect(() => {
    fetchStatus();
    
    // Check URL params for OAuth callback result
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_fit') === 'connected') {
      toast.success('Google Fit connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('google_fit') === 'error') {
      toast.error(`Connection failed: ${params.get('message') || 'Unknown error'}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.connected) {
      fetchData();
    }
  }, [selectedDays, status?.connected]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/integrations/google-fit/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate Google Fit connection');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/integrations/google-fit/disconnect`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Google Fit disconnected');
      setStatus({ connected: false });
      setData(null);
      setTodayStats(null);
    } catch (error) {
      toast.error('Failed to disconnect Google Fit');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(
        `${API}/integrations/google-fit/sync?days=${selectedDays}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Synced ${response.data.synced_days} days of data!`);
      await fetchData();
      await fetchTodayStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync data');
    } finally {
      setSyncing(false);
    }
  };

  const handleAutoSyncToggle = async (enabled) => {
    try {
      await axios.put(
        `${API}/integrations/google-fit/settings?auto_sync=${enabled}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(prev => ({ ...prev, auto_sync: enabled }));
      toast.success(`Auto-sync ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="google-fit-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            GOOGLE <span className="text-primary">FIT</span>
          </h1>
          <p className="text-gray-400 mt-1">Sync your fitness data from Google Fit</p>
        </div>
        
        {status?.connected && (
          <Button
            onClick={handleSync}
            disabled={syncing}
            variant="outline"
            className="border-border hover:border-primary"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sync Now
          </Button>
        )}
      </div>

      {/* Connection Status */}
      <div className={`card-tactical ${status?.connected ? 'border-green-500/50' : 'border-yellow-500/50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
              status?.connected ? 'bg-green-500/20' : 'bg-yellow-500/20'
            }`}>
              {status?.connected ? (
                <CheckCircle2 className="w-7 h-7 text-green-500" />
              ) : (
                <AlertCircle className="w-7 h-7 text-yellow-500" />
              )}
            </div>
            <div>
              <h2 className="text-lg text-white font-semibold">
                {status?.connected ? 'Connected to Google Fit' : 'Not Connected'}
              </h2>
              {status?.connected ? (
                <p className="text-sm text-gray-400">
                  {status.email} • Last sync: {status.last_sync ? new Date(status.last_sync).toLocaleString() : 'Never'}
                </p>
              ) : (
                <p className="text-sm text-gray-400">
                  Connect to sync steps, calories, and active minutes
                </p>
              )}
            </div>
          </div>
          
          {status?.connected ? (
            <Button
              onClick={handleDisconnect}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
            >
              <Unlink className="w-4 h-4 mr-2" />
              Disconnect
            </Button>
          ) : (
            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {connecting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              Connect Google Fit
            </Button>
          )}
        </div>
        
        {status?.connected && (
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Switch
                checked={status.auto_sync}
                onCheckedChange={handleAutoSyncToggle}
              />
              <Label className="text-gray-300">Auto-sync daily</Label>
            </div>
          </div>
        )}
      </div>

      {/* Today's Stats */}
      {status?.connected && todayStats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card-tactical text-center">
            <Footprints className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <div className="font-mono text-3xl text-white">{todayStats.steps?.toLocaleString() || 0}</div>
            <div className="text-xs text-gray-400 uppercase mt-1">Steps Today</div>
          </div>
          <div className="card-tactical text-center">
            <Flame className="w-8 h-8 text-orange-500 mx-auto mb-2" />
            <div className="font-mono text-3xl text-white">{todayStats.calories?.toLocaleString() || 0}</div>
            <div className="text-xs text-gray-400 uppercase mt-1">Calories Burned</div>
          </div>
          <div className="card-tactical text-center">
            <Timer className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <div className="font-mono text-3xl text-white">{todayStats.active_minutes || 0}</div>
            <div className="text-xs text-gray-400 uppercase mt-1">Active Minutes</div>
          </div>
        </div>
      )}

      {/* Historical Data */}
      {status?.connected && data && (
        <div className="card-tactical">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white uppercase flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Activity History
            </h3>
            <Select value={selectedDays} onValueChange={setSelectedDays}>
              <SelectTrigger className="w-32 bg-secondary border-transparent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Totals Summary */}
          {data.totals && (
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-secondary/30 rounded-sm">
              <div className="text-center">
                <div className="text-2xl font-mono text-blue-400">{data.totals.steps?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Total Steps</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-mono text-orange-400">{data.totals.calories?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Total Calories</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-mono text-green-400">{data.totals.active_minutes}</div>
                <div className="text-xs text-gray-500">Total Active Min</div>
              </div>
            </div>
          )}

          {/* Daily Breakdown */}
          {data.daily_data && data.daily_data.length > 0 ? (
            <div className="space-y-2">
              {data.daily_data.slice().reverse().map((day, index) => (
                <div 
                  key={day.date}
                  className="flex items-center justify-between p-3 bg-secondary/20 rounded-sm border border-border"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-white">
                      {new Date(day.date).toLocaleDateString('en-US', { 
                        weekday: 'short', month: 'short', day: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-1">
                      <Footprints className="w-4 h-4 text-blue-400" />
                      <span className="text-gray-300 font-mono">{day.steps?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Flame className="w-4 h-4 text-orange-400" />
                      <span className="text-gray-300 font-mono">{day.calories?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Timer className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300 font-mono">{day.active_minutes} min</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No activity data found</p>
              <p className="text-sm">Sync your Google Fit data to see your activity history</p>
            </div>
          )}
        </div>
      )}

      {/* Not Connected State */}
      {!status?.connected && (
        <div className="card-tactical text-center py-12">
          <Activity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl text-white font-semibold mb-2">Track Your Daily Activity</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Connect Google Fit to automatically sync your steps, calories burned, and active minutes from your phone or wearable device.
          </p>
          <Button
            onClick={handleConnect}
            disabled={connecting}
            size="lg"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {connecting ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Link2 className="w-5 h-5 mr-2" />
            )}
            Connect Google Fit
          </Button>
          <p className="text-xs text-gray-500 mt-4">
            Supported data: Steps, Calories, Active Minutes
          </p>
        </div>
      )}
    </div>
  );
}
